import React from 'react';
import { ORB_TYPES, SPECIALS } from '../constants';
import { Volume2, VolumeX, RotateCcw, Trophy } from 'lucide-react';

interface UIOverlayProps {
  score: number;
  highScore: number;
  nextOrbIndex: number;
  futureOrbIndex: number; 
  level: number;
  progress: number; 
  isMuted: boolean;
  onToggleMute: () => void;
  onReset: () => void;
}

const UIOverlay: React.FC<UIOverlayProps> = ({ 
  score, 
  highScore,
  nextOrbIndex, 
  futureOrbIndex,
  level,
  progress,
  isMuted, 
  onToggleMute, 
  onReset,
}) => {
  
  const renderOrb = (index: number, size: number) => {
    if (index === SPECIALS.DEBRIS.id) {
        return <div style={{ width: size, height: size }} className="bg-slate-500 clip-hexagon animate-pulse shadow-lg" />;
    }
    if (index === SPECIALS.ANTIMATTER.id) {
        return <div style={{ width: size, height: size }} className="rounded-full bg-white shadow-[0_0_15px_#0ff] animate-pulse" />;
    }

    const type = ORB_TYPES[index];
    if (!type) return null;

    return (
        <div 
            className="rounded-full shadow-lg relative z-10 transition-all duration-300"
            style={{ 
                width: size, 
                height: size, 
                background: `radial-gradient(circle at 35% 35%, #fff 0%, ${type.color} 40%, ${type.glowColor} 100%)`,
                boxShadow: `0 0 10px ${type.glowColor}80`
            }} 
        />
    );
  };

  const getNextOrbVisual = () => {
    const mainScale = 0.75;
    const mainRadius = (ORB_TYPES[nextOrbIndex]?.radius || 20) * 2 * mainScale;
    const futureScale = 0.4;
    const futureRadius = (ORB_TYPES[futureOrbIndex]?.radius || 20) * 2 * futureScale;

    return (
        <div className="flex items-center justify-center gap-2">
            <div className="relative">
                {renderOrb(nextOrbIndex, Math.min(48, mainRadius))}
            </div>
            <div className="opacity-50 scale-75 border border-white/10 rounded-full p-1 bg-slate-900/50">
                {renderOrb(futureOrbIndex, Math.min(24, futureRadius))}
            </div>
        </div>
    );
  };

  return (
    <div className="flex flex-col w-full h-full gap-2">
      <div className="flex items-center justify-between w-full px-2">
        
        <div className="flex flex-col min-w-[100px]">
            <div className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-0.5 flex items-center gap-1">
                Current Mass
            </div>
            <div className="text-2xl leading-none font-black text-white tabular-nums tracking-tight drop-shadow-md">
                {score.toLocaleString()}
            </div>
            {highScore > 0 && (
                <div className="text-[10px] text-yellow-500/90 font-medium flex items-center gap-1 mt-1">
                   <Trophy size={10} /> BEST: {highScore.toLocaleString()}
                </div>
            )}
        </div>

        <div className="flex flex-col items-center justify-center -mt-2">
            <div className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.2em] mb-1.5 opacity-80">
                Queue
            </div>
            <div className="px-3 h-16 rounded-xl bg-slate-800/60 border border-slate-700/50 shadow-[inset_0_2px_15px_rgba(0,0,0,0.5)] flex items-center justify-center relative overflow-hidden backdrop-blur-md">
                <div className="absolute inset-0 opacity-20" 
                     style={{ 
                        backgroundImage: 'radial-gradient(#94a3b8 1px, transparent 1px)',
                        backgroundSize: '8px 8px' 
                     }} 
                />
                
                {getNextOrbVisual()}
                
                <div className="absolute top-1 left-1 w-1.5 h-1.5 border-t border-l border-white/20 rounded-tl-sm" />
                <div className="absolute top-1 right-1 w-1.5 h-1.5 border-t border-r border-white/20 rounded-tr-sm" />
                <div className="absolute bottom-1 left-1 w-1.5 h-1.5 border-b border-l border-white/20 rounded-bl-sm" />
                <div className="absolute bottom-1 right-1 w-1.5 h-1.5 border-b border-r border-white/20 rounded-tr-sm" />
            </div>
        </div>

        <div className="flex items-center gap-1.5 justify-end min-w-[100px]">
             <button 
                onClick={onToggleMute}
                className="w-9 h-9 rounded-lg bg-slate-800/50 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-all border border-transparent active:scale-95"
                aria-label="Toggle Mute"
            >
                {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
            </button>
            <button 
                onClick={onReset}
                className="w-9 h-9 rounded-lg bg-slate-800/50 hover:bg-red-500/20 text-slate-400 hover:text-red-400 flex items-center justify-center transition-all border border-transparent active:scale-95"
                aria-label="Reset Game"
            >
                <RotateCcw size={16} />
            </button>
        </div>
      </div>

      <div className="w-full px-2 flex flex-col gap-1">
          <div className="flex justify-between items-end">
              <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-widest">
                  Stage {level}
              </span>
              <span className="text-[9px] text-slate-500 font-mono">
                  {(progress * 100).toFixed(0)}%
              </span>
          </div>
          <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden border border-slate-700/50">
              <div 
                  className="h-full bg-gradient-to-r from-indigo-500 to-cyan-400 transition-all duration-700 ease-out shadow-[0_0_10px_rgba(99,102,241,0.5)]"
                  style={{ width: `${progress * 100}%` }}
              />
          </div>
      </div>

      <style>{`
            .clip-hexagon {
                clip-path: polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%);
            }
      `}</style>
    </div>
  );
};

export default UIOverlay;